import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';
import API from '../config';
import BottomNav from '../components/BottomNav';
import { Helmet } from 'react-helmet-async';


const useInView = (threshold = 0.08) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0, style = {} }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

const NotificationBell = ({ token, T }) => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchNotifications = async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [nr, cr] = await Promise.all([
        axios.get(`${API}/api/notifications`, { headers: h }),
        axios.get(`${API}/api/notifications/unread-count`, { headers: h })
      ]);
      setNotifications(nr.data);
      setUnread(cr.data.count);
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/api/notifications/mark-all-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUnread(0);
      setNotifications(p => p.map(n => ({ ...n, is_read: true })));
    } catch (_) {}
  };

  const handleOpen = () => { setOpen(!open); if (!open && unread > 0) markAllRead(); };
  const ago = ts => {
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return `${s}s`; if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`; return `${Math.floor(s / 86400)}d`;
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={handleOpen} style={{
        background: open ? T.surfaceHi : 'transparent',
        border: `1px solid ${open ? T.textMuted : T.border}`,
        borderRadius: 8, padding: '7px 11px', cursor: 'pointer',
        color: T.textMuted, fontSize: 15, position: 'relative', transition: 'all 0.2s'
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && <span style={{ position: 'absolute', top: -4, right: -4, background: T.no, color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 100, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 320, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: '0 24px 64px rgba(0,0,0,0.7)', zIndex: 200, overflow: 'hidden', animation: 'slideDown 0.18s ease' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 600, fontSize: 13, color: T.text }}>Notifications</p>
            {notifications.length > 0 && <span onClick={markAllRead} style={{ fontSize: 11, color: T.textMuted, cursor: 'pointer', fontFamily: "'Outfit', sans-serif" }}>Mark read</span>}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {notifications.length === 0
              ? <div style={{ padding: '28px 16px', textAlign: 'center', color: T.textDim, fontSize: 13, fontFamily: "'Outfit', sans-serif" }}>No notifications</div>
              : notifications.map(n => (
                <div key={n.id} onClick={() => { if (n.market_id) { navigate(`/markets/${n.market_id}`); setOpen(false); } }}
                  style={{ padding: '11px 16px', borderBottom: `1px solid ${T.border}`, background: n.is_read ? 'transparent' : T.accentDim, cursor: n.market_id ? 'pointer' : 'default', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: n.is_read ? T.textDim : (n.type === 'won' ? T.yes : n.type === 'admin_message' ? T.accent : T.no), marginTop: 6, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5, marginBottom: 3, fontFamily: "'Outfit', sans-serif" }}>{n.message}</p>
                    <p style={{ fontSize: 11, color: T.textDim, fontFamily: 'monospace' }}>{ago(n.created_at)}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Skel = ({ w = '100%', h = 16, r = 5 }) => {
  const { T } = useTheme();
  return (
    <div style={{ width: w, height: h, borderRadius: r, background: `linear-gradient(90deg, ${T.surfaceHi} 25%, ${T.border} 50%, ${T.surfaceHi} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.6s infinite' }} />
  );
};

const MiniSparkline = ({ values, color, width = 80, height = 32 }) => {
  if (!values || values.length < 2) return null;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
    </svg>
  );
};

const PnLChart = ({ bets, T }) => {
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const resolvedBets = bets
    .filter(b => b.status === 'won' || b.status === 'lost' || b.status === 'sold')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  if (resolvedBets.length < 2) {
    return (
      <div style={{ height: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: T.textDim, gap: 8 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.border} strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        <span style={{ fontSize: 12, fontFamily: "'Outfit', sans-serif" }}>P&L chart appears after first resolved trade</span>
      </div>
    );
  }

  let cumulative = 0;
  const points = resolvedBets.map(b => {
    const pnl = b.status === 'won' ? parseFloat(b.payout || 0) - parseFloat(b.amount || 0)
              : b.status === 'sold' ? parseFloat(b.payout || 0) - parseFloat(b.amount || 0)
              : -parseFloat(b.amount || 0);
    cumulative += pnl;
    return { date: new Date(b.created_at), pnl: cumulative, raw: pnl };
  });

  const W = 560, H = 120, PL = 8, PR = 8, PT = 12, PB = 20;
  const CW = W - PL - PR, CH = H - PT - PB;
  const vals = points.map(p => p.pnl);
  const minV = Math.min(0, Math.min(...vals)), maxV = Math.max(0, Math.max(...vals));
  const range = maxV - minV || 1;
  const toX = i => PL + (i / (points.length - 1)) * CW;
  const toY = v => PT + CH - ((v - minV) / range) * CH;
  const zeroY = toY(0);
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i).toFixed(1)} ${toY(p.pnl).toFixed(1)}`).join(' ');
  const lastVal = vals[vals.length - 1];
  const lineColor = lastVal >= 0 ? T.yes : T.no;

  const handleMove = e => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round(((mx - PL) / CW) * (points.length - 1));
    if (idx < 0 || idx >= points.length) { setTooltip(null); return; }
    setTooltip({ x: toX(idx), y: toY(points[idx].pnl), val: points[idx].pnl, raw: points[idx].raw, date: points[idx].date });
  };

  return (
    <div>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', cursor: 'crosshair', overflow: 'visible' }} onMouseMove={handleMove} onMouseLeave={() => setTooltip(null)}>
        <defs>
          <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.12"/>
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.01"/>
          </linearGradient>
          <clipPath id="pnlClip"><rect x={PL} y={PT} width={CW} height={CH}/></clipPath>
        </defs>
        <line x1={PL} y1={zeroY} x2={PL + CW} y2={zeroY} stroke={T.border} strokeWidth="1" strokeDasharray="3,4"/>
        <path d={`${pathD} L ${toX(points.length-1)} ${zeroY} L ${PL} ${zeroY} Z`} fill="url(#pnlGrad)" clipPath="url(#pnlClip)"/>
        <path d={pathD} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#pnlClip)" pathLength="1" strokeDasharray="1" strokeDashoffset="1" style={{ animation: 'drawLine 1.2s cubic-bezier(0.4,0,0.2,1) forwards' }}/>
        <circle cx={toX(points.length-1)} cy={toY(lastVal)} r="3.5" fill={lineColor}/>
        {tooltip && (
          <>
            <line x1={tooltip.x} y1={PT} x2={tooltip.x} y2={PT+CH} stroke={T.border} strokeWidth="1" strokeDasharray="3,3"/>
            <circle cx={tooltip.x} cy={tooltip.y} r="3.5" fill={lineColor} stroke={T.bg} strokeWidth="2"/>
          </>
        )}
      </svg>
      {tooltip && (
        <div style={{ background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 6, marginTop: 6 }}>
          <span style={{ fontSize: 11, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>{tooltip.date.toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
          <span style={{ fontSize: 11, color: tooltip.raw >= 0 ? T.yes : T.no, fontWeight: 700, fontFamily: 'monospace' }}>{tooltip.raw >= 0 ? '+' : ''}KES {tooltip.raw.toFixed(2)}</span>
          <span style={{ fontSize: 11, color: tooltip.val >= 0 ? T.yes : T.no, fontWeight: 700, fontFamily: 'monospace' }}>Total: {tooltip.val >= 0 ? '+' : ''}KES {tooltip.val.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};
// Mirrors MarketPage's getProbs() exactly
const getMarketProbs = (m, priceHistory) => {
  if (m.market_type === 'multiple' && m.options?.length) {
    const rawRows = priceHistory?.data || [];
    const totalPool = m.options.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
    return m.options.map(o => {
      const optRows = rawRows.filter(r => String(r.option_id) === String(o.id));
      const latestPct = optRows.length ? parseFloat(optRows[optRows.length - 1].percent) : null;
      return {
        ...o,
        prob: latestPct !== null
          ? Math.round(latestPct)
          : totalPool > 0
            ? Math.round((parseFloat(o.total_amount || 0) / totalPool) * 100)
            : Math.round(100 / m.options.length),
      };
    });
  }
  const y = parseFloat(m.total_yes || 0), n = parseFloat(m.total_no || 0), t = y + n;
  const yp = m.yes_price ? Math.round(parseFloat(m.yes_price) * 100) : t > 0 ? Math.round((y / t) * 100) : 50;
  return [{ label: 'YES', prob: yp }, { label: 'NO', prob: 100 - yp }];
};

const TrendingMarkets = ({ token }) => {
  const { T } = useTheme();
  const [markets, setMarkets] = useState([]);
  const [historyMap, setHistoryMap] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/markets?limit=4`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        const list = r.data?.markets || r.data || [];
        setMarkets(list);
        const entries = await Promise.all(list.map(m =>
          axios.get(`${API}/api/markets/${m.id}/price-history`).then(res => [m.id, res.data]).catch(() => [m.id, null])
        ));
        setHistoryMap(Object.fromEntries(entries));
      }).catch(() => {});
  }, [token]);

  if (!markets.length) return <p style={{ fontSize: 12, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>Loading…</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {markets.slice(0, 4).map((m, i) => {
        const isMulti = m.market_type === 'multiple';
        const probs = getMarketProbs(m, historyMap[m.id]);

        let label, prob, leading;
        if (isMulti) {
          const top = probs.reduce((a, b) => (b.prob > a.prob ? b : a), probs[0]);
          label = top.label;
          prob = top.prob;
          leading = true;
        } else {
          const yp = probs[0].prob;
          leading = yp >= 50;
          prob = leading ? yp : 100 - yp;
          label = leading ? (m.yes_label || 'YES') : (m.no_label || 'NO');
        }

        const color = isMulti ? T.accent : (leading ? T.yes : T.no);

        return (
          <div key={m.id} onClick={() => navigate(`/markets/${m.id}`)}
            style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '9px 0', cursor: 'pointer', borderBottom: i < 3 ? `1px solid ${T.border}` : 'none', transition: 'opacity 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.6'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            <p style={{ fontSize: 11, color: T.text, fontWeight: 500, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontFamily: "'Outfit', sans-serif" }}>{m.title}</p>
            <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'monospace', padding: '2px 7px', borderRadius: 4, background: `${color}14`, color, whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>{label} {prob}%</span>
          </div>
        );
      })}
    </div>
  );
};

const RecentActivity = ({ bets }) => {
  const { T } = useTheme();
  const navigate = useNavigate();
  const sorted = [...bets].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
  if (!sorted.length) return <p style={{ fontSize: 12, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>No activity yet</p>;

  const ago = ts => {
    const s = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const dotColor = b => {
    if (b.status === 'won') return T.yes;
    if (b.status === 'lost') return T.no;
    if (b.status === 'sold') return T.gold;
    return T.accent;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {sorted.map((b, i) => (
        <div key={b.id} onClick={() => navigate(`/markets/${b.market_id}`)}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', cursor: 'pointer', borderBottom: i < sorted.length - 1 ? `1px solid ${T.border}` : 'none', transition: 'opacity 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.6'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.border, marginTop: 5, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 11, color: T.text, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', fontFamily: "'Outfit', sans-serif" }}>
              {b.status === 'won' ? `Won on "${b.title}"` : b.status === 'lost' ? `Lost on "${b.title}"` : b.status === 'sold' ? `Sold position on "${b.title}"` : `Trade placed on "${b.title}"`}
            </p>
            <p style={{ fontSize: 10, color: T.textDim, marginTop: 2, fontFamily: "'Outfit', sans-serif" }}>KES {parseFloat(b.amount).toFixed(0)} · {ago(b.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const CollapsibleSection = ({ title, isMobile, children }) => {
  const { T } = useTheme();
  const [open, setOpen] = useState(true);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={() => isMobile && setOpen(o => !o)}
        style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: isMobile ? 'pointer' : 'default', borderBottom: open ? `1px solid ${T.border}` : 'none' }}>
        <p style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: "'Outfit', sans-serif" }}>{title}</p>
        {isMobile && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>}
      </div>
      {open && <div style={{ padding: '12px 14px' }}>{children}</div>}
    </div>
  );
};

const BetRow = ({ bet, isMobile }) => {
  const { T } = useTheme();
  const navigate = useNavigate();
  const isWon = bet.status === 'won', isLost = bet.status === 'lost', isSold = bet.status === 'sold';
  const isPending = bet.status === 'active' && bet.market_status === 'closed';
  const statusColor = isWon ? T.yes : isLost ? T.no : isSold ? T.gold : isPending ? T.accent : T.gold;
  const statusLabel = isPending ? 'PENDING' : bet.status.toUpperCase();
  const pred = (bet.option_label || (bet.prediction === 'yes' ? (bet.yes_label || 'YES') : (bet.prediction === 'no' ? (bet.no_label || 'NO') : bet.prediction)) || '').toUpperCase();
  const predColor = bet.prediction === 'yes' ? T.yes : bet.prediction === 'no' ? T.no : T.accent;

  const getPayout = () => {
    if (bet.status !== 'active' || !bet.potential_payout) return null;
    const payout = parseFloat(bet.potential_payout || 0);
    const amount = parseFloat(bet.amount || 0);
    const profit = payout - amount;
    const pct = amount > 0 ? Math.round((profit / amount) * 100) : 0;
    return { payout, profit, pct };
  };
  const live = getPayout();
  const pnl = (isWon || isSold) ? parseFloat(bet.payout || 0) - parseFloat(bet.amount || 0) : isLost ? -parseFloat(bet.amount || 0) : null;

  return (
    <div onClick={() => navigate(`/markets/${bet.market_id}`)}
      style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '2fr 1fr 1fr 1fr 100px', alignItems: 'center', gap: isMobile ? 8 : 12, padding: isMobile ? '12px 16px' : '12px 20px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', transition: 'background 0.15s', overflow: 'hidden', minWidth: 0 }}
      onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 7, overflow: 'hidden', flexShrink: 0, background: T.surfaceHi, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {bet.image_url
            ? <img src={bet.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
            : <span style={{ fontSize: 8, fontWeight: 800, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>{(bet.category || 'MKT').slice(0, 3).toUpperCase()}</span>
          }
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{ fontWeight: 600, fontSize: 12, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3, fontFamily: "'Outfit', sans-serif" }}>{bet.title}</p>
          <span style={{ fontSize: 10, color: predColor, fontWeight: 700, fontFamily: 'monospace', background: `${predColor}12`, padding: '1px 5px', borderRadius: 3 }}>{pred}</span>
        </div>
      </div>
      {!isMobile && (
        <>
          <div>
            <p style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stake</p>
            <p style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: 'monospace' }}>KES {parseFloat(bet.amount).toLocaleString()}</p>
          </div>
          <div>
            {live ? (
              <>
                <p style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>If correct</p>
                <p style={{ fontSize: 13, fontWeight: 700, color: live.profit >= 0 ? T.yes : T.no, fontFamily: 'monospace' }}>KES {live.payout.toFixed(2)}</p>
              </>
            ) : pnl !== null ? (
              <>
                <p style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>P&L</p>
                <p style={{ fontSize: 13, fontWeight: 700, color: pnl >= 0 ? T.yes : T.no, fontFamily: 'monospace' }}>{pnl >= 0 ? '+' : ''}KES {Math.abs(pnl).toFixed(2)}</p>
              </>
            ) : <p style={{ fontSize: 11, color: T.textDim }}>—</p>}
          </div>
          <div>
            <p style={{ fontSize: 10, color: T.textDim, marginBottom: 3, fontFamily: "'Outfit', sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</p>
            <p style={{ fontSize: 11, color: T.textMuted, fontFamily: 'monospace' }}>{new Date(bet.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</p>
          </div>
        </>
      )}
      <div style={{ textAlign: isMobile ? 'right' : 'center' }}>
        <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: `${statusColor}14`, color: statusColor, fontWeight: 700, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{statusLabel}</span>
        {isMobile && pnl !== null && <p style={{ fontSize: 11, fontWeight: 700, color: pnl >= 0 ? T.yes : T.no, fontFamily: 'monospace', marginTop: 4 }}>{pnl >= 0 ? '+' : ''}KES {Math.abs(pnl).toFixed(0)}</p>}
      </div>
    </div>
  );
};

const SavedMarketRow = ({ market, onUnsave }) => {
  const { T } = useTheme();
  const navigate = useNavigate();
  const yes = parseFloat(market.total_yes || 0), no = parseFloat(market.total_no || 0);
  const total = yes + no;
  const isMulti = market.market_type === 'multiple';
  const leadingOption = isMulti && market.options?.length
    ? market.options.reduce((a, b) => parseFloat(a.total_amount || 0) > parseFloat(b.total_amount || 0) ? a : b) : null;
  const optTotal = isMulti ? market.options?.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0) || 1 : 1;
  const prob = isMulti && leadingOption
    ? Math.round((parseFloat(leadingOption.total_amount || 0) / optTotal) * 100)
    : market.yes_price ? Math.round(parseFloat(market.yes_price) * 100) : total > 0 ? Math.round((yes / total) * 100) : 50;
  const probLabel = isMulti && leadingOption ? leadingOption.label : prob >= 50 ? (market.yes_label || 'YES') : (market.no_label || 'NO');
  const probColor = prob >= 50 ? T.yes : T.no;
  const daysLeft = market.closes_at ? Math.ceil((new Date(market.closes_at) - Date.now()) / 86400000) : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: `1px solid ${T.border}`, transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div onClick={() => navigate(`/markets/${market.id}`)} style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: T.surfaceHi, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        {market.image_url
          ? <img src={market.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
          : <span style={{ fontSize: 9, fontWeight: 800, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>{(market.category || 'MKT').slice(0, 3).toUpperCase()}</span>
        }
      </div>
      <div onClick={() => navigate(`/markets/${market.id}`)} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <p style={{ fontWeight: 600, fontSize: 13, color: T.text, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: "'Outfit', sans-serif" }}>{market.title}</p>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: probColor, fontWeight: 700, background: `${probColor}12`, border: `1px solid ${probColor}28`, borderRadius: 4, padding: '1px 6px', fontFamily: 'monospace' }}>{probLabel} {prob}%</span>
          <span style={{ fontSize: 10, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>{market.category}</span>
          {daysLeft !== null && <span style={{ fontSize: 10, color: daysLeft < 7 ? T.no : T.textDim, fontFamily: "'Outfit', sans-serif" }}>{daysLeft <= 0 ? 'Ended' : `${daysLeft}d left`}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: 'monospace' }}>KES {total.toLocaleString()}</p>
        <button onClick={e => { e.stopPropagation(); onUnsave(market.id); }}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.textDim, padding: 2, transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = T.no}
          onMouseLeave={e => e.currentTarget.style.color = T.textDim}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
        </button>
      </div>
    </div>
  );
};

const WinRateDonut = ({ stats, activeBets, lostBets, soldBets }) => {
  const { T } = useTheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  if (!stats?.total_bets || stats.total_bets === 0) return null;

  const winRate = Math.round((stats.won_bets / stats.total_bets) * 100);

  const segments = [
    { label: 'Won',  value: stats.won_bets,    color: '#16c784' },
    { label: 'Lost', value: lostBets.length,   color: '#ea3943' },
    { label: 'Sold', value: soldBets.length,   color: '#f3a94a' },
    { label: 'Open', value: activeBets.length, color: '#3772ff' },
  ].filter(s => s.value > 0);

useEffect(() => {
  if (!canvasRef.current) return;
  if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

  const segs = [
    { label: 'Won',  value: stats.won_bets,    color: '#16c784' },
    { label: 'Lost', value: lostBets.length,   color: '#ea3943' },
    { label: 'Sold', value: soldBets.length,   color: '#f3a94a' },
    { label: 'Open', value: activeBets.length, color: '#3772ff' },
  ].filter(s => s.value > 0);

  if (!segs.length) return;

  chartRef.current = new Chart(canvasRef.current, {
    type: 'doughnut',
    data: {
      labels: segs.map(s => s.label),
      datasets: [{
        data: segs.map(s => s.value),
        backgroundColor: segs.map(s => s.color),
        borderWidth: 3,
        borderColor: T.surface,
        hoverOffset: 4,
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.label}: ${ctx.raw} trades`
          }
        }
      }
    }
  });

  return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
}, [stats, lostBets.length, soldBets.length, activeBets.length, T.surface]);

  return (
<div style={{
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  padding: '16px',
  overflow: 'hidden',
  width: '100%',
  boxSizing: 'border-box',
}}>
      {/* Legend row — matches the screenshot style */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginBottom: 14 }}>
        {segments.map(s => (
          <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textMuted, fontFamily: "'Outfit', sans-serif" }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            {s.label} {Math.round((s.value / stats.total_bets) * 100)}%
          </span>
        ))}
      </div>

      {/* Donut + center label */}
      <div style={{ position: 'relative', height: 180, width: '100%', maxWidth: '100%', margin: '0 auto' }}>
  <canvas ref={canvasRef} />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: winRate >= 50 ? '#16c784' : '#ea3943', fontFamily: "'Syne', sans-serif", lineHeight: 1 }}>
            {winRate}%
          </span>
          <span style={{ fontSize: 11, color: T.textDim, marginTop: 3, fontFamily: "'Outfit', sans-serif" }}>
            win rate
          </span>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { T } = useTheme();
  const [profile, setProfile]           = useState(null);
  const [stats, setStats]               = useState(null);
  const [bets, setBets]                 = useState([]);
  const [savedMarkets, setSavedMarkets] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [betFilter, setBetFilter]       = useState('all');
  const [activeTab, setActiveTab]       = useState('portfolio');
  const [menuOpen, setMenuOpen]         = useState(false);
  const { token, logout, user }         = useAuth();
  const navigate                        = useNavigate();
  const isMobile                        = useMobile();

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetchData();
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } });
        setProfile(r.data);
      } catch (_) {}
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchData = async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [pr, sr, br] = await Promise.all([
        axios.get(`${API}/api/users/profile`, { headers: h }),
        axios.get(`${API}/api/users/stats`,   { headers: h }),
        axios.get(`${API}/api/bets/my-bets`,  { headers: h }),
      ]);
      setProfile(pr.data);
      setStats(sr.data);
      setBets(br.data);
      try {
        const wr = await axios.get(`${API}/api/markets/user/bookmarks`, { headers: h });
        setSavedMarkets(wr.data);
      } catch (_) {}
    } catch (err) {
      if (err.response?.status === 401) { logout(); navigate('/login'); }
    } finally { setLoading(false); }
  };

  const handleUnsave = async (marketId) => {
    try {
      await axios.post(`${API}/api/markets/${marketId}/bookmark`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSavedMarkets(prev => prev.filter(m => m.id !== marketId));
    } catch (_) {}
  };

  const activeBets      = bets.filter(b => b.status === 'active');
  const wonBets         = bets.filter(b => b.status === 'won');
  const lostBets        = bets.filter(b => b.status === 'lost');
  const soldBets        = bets.filter(b => b.status === 'sold');
  const resolvedBets    = [...wonBets, ...lostBets, ...soldBets];
  const totalStaked     = bets.reduce((s, b) => s + parseFloat(b.amount || 0), 0);
  const totalReturned   = wonBets.reduce((s, b) => s + parseFloat(b.payout || 0), 0) + soldBets.reduce((s, b) => s + parseFloat(b.payout || 0), 0);
  const totalDeployed   = activeBets.reduce((s, b) => s + parseFloat(b.amount || 0), 0);
  const unrealizedValue = activeBets.reduce((s, b) => s + parseFloat(b.potential_payout || 0), 0);
  const unrealizedPnL   = unrealizedValue - totalDeployed;
  const realizedPnL     = totalReturned - resolvedBets.reduce((s, b) => s + parseFloat(b.amount || 0), 0);
  const winRate         = stats?.total_bets > 0 ? Math.round((stats.won_bets / stats.total_bets) * 100) : 0;
  const roi             = totalStaked > 0 ? ((totalReturned - resolvedBets.reduce((s, b) => s + parseFloat(b.amount || 0), 0)) / totalStaked * 100) : 0;

  const bestTrade = wonBets.length > 0
    ? wonBets.reduce((best, b) => {
        const pnl = parseFloat(b.payout || 0) - parseFloat(b.amount || 0);
        const bestPnl = parseFloat(best.payout || 0) - parseFloat(best.amount || 0);
        return pnl > bestPnl ? b : best;
      }) : null;

  const worstTrade = lostBets.length > 0
    ? lostBets.reduce((worst, b) => parseFloat(b.amount || 0) > parseFloat(worst.amount || 0) ? b : worst) : null;

  const getStreak = () => {
    const sorted = resolvedBets.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (!sorted.length) return { count: 0, type: null };
    const first = sorted[0].status;
    if (first !== 'won' && first !== 'lost') return { count: 0, type: null };
    let count = 0;
    for (const b of sorted) { if (b.status === first) count++; else break; }
    return { count, type: first };
  };
  const streak = getStreak();

  const isAdmin     = profile?.is_admin || user?.is_admin || false;
  const displayName = user?.username ? `@${user.username}` : profile?.full_name?.split(' ')[0] || 'Trader';

  const navItems = [
    { label: 'Deposit',     path: '/deposit' },
    { label: 'Withdraw',    path: '/withdraw' },
    { label: 'Markets',     path: '/markets' },
    { label: 'Chama',       path: '/chama' },
    { label: 'Leaderboard', path: '/leaderboard' },
    ...(isAdmin ? [{ label: 'Admin', path: '/admin' }] : []),
    { label: 'Profile',     path: '/profile' },
  ];

  const TABS = [
    { key: 'portfolio', label: 'Portfolio' },
    { key: 'trades',    label: 'Trades',  count: bets.length },
    { key: 'saved',     label: 'Saved',   count: savedMarkets.length },
  ];

  const filteredBets = bets.filter(b => betFilter === 'all' || b.status === betFilter);

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'Outfit', sans-serif", color: T.text, position: 'relative', paddingBottom: isMobile ? 80 : 0, overflowX: isMobile ? 'auto' : 'visible' }}>

<Helmet>
  <title>Dashboard · Mino</title>
  <meta name="robots" content="noindex, nofollow" />
  <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
</Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.1); }
        ::-webkit-scrollbar { width: 3px; height: 3px; }
        .mino-aurora { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
        .mino-aurora::before {
          content: ''; position: absolute; width: 140%; height: 55%; top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%, transparent 0deg, rgba(55,114,255,0.07) 40deg, rgba(22,199,132,0.05) 80deg, rgba(124,58,237,0.06) 120deg, transparent 160deg);
          filter: blur(60px); animation: auroraShift 20s ease-in-out infinite alternate; transform-origin: center top;
        }
        .mino-aurora::after {
          content: ''; position: absolute; width: 80%; height: 40%; bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%, transparent 0deg, rgba(22,199,132,0.04) 50deg, rgba(55,114,255,0.04) 100deg, transparent 150deg);
          filter: blur(70px); animation: auroraShift2 26s ease-in-out infinite alternate;
        }
        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }
        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; background: transparent; border: none; }
        @keyframes auroraShift { 0%{transform:rotate(-4deg) scaleX(1);opacity:1} 50%{transform:rotate(2deg) scaleX(1.08);opacity:0.85} 100%{transform:rotate(-6deg) scaleX(0.96);opacity:1} }
        @keyframes auroraShift2 { 0%{transform:rotate(6deg) scaleX(1);opacity:0.8} 50%{transform:rotate(-3deg) scaleX(1.1);opacity:1} 100%{transform:rotate(8deg) scaleX(0.92);opacity:0.75} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideDown { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes drawLine { from{stroke-dashoffset:1} to{stroke-dashoffset:0} }
        @keyframes ticker { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        .ticker-track { display: inline-flex; animation: ticker 38s linear infinite; white-space: nowrap; will-change: transform; }
        .ticker-track:hover { animation-play-state: paused; }
        .stat-card { transition: border-color 0.25s ease, background 0.25s ease; }
        .tab-btn { transition: all 0.15s ease; }
        .scroll-container { -ms-overflow-style: none; scrollbar-width: none; }
        .scroll-container::-webkit-scrollbar { display: none; }
        .horizontal-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; scroll-snap-type: x proximity; touch-action: pan-x; }
        .horizontal-scroll > * { scroll-snap-align: start; }
        .horizontal-scroll::-webkit-scrollbar { display: none; }
        @media (max-width: 640px) {
  .tab-btn { padding: 12px 10px !important; font-size: 12px !important; }
  .horizontal-scroll { -webkit-overflow-scrolling: touch; }
}
      `}</style>

      <div className="mino-aurora" />
      <div className="mino-dot-grid" />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '0 16px' : '0 32px', background: `${T.bg}cc`, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: `1px solid ${T.border}` }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: T.text }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!isMobile && navItems.map(item => (
            <button key={item.path} className="mino-nav-link" style={{ color: T.textMuted }}
              onClick={() => navigate(item.path)}
              onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.background = T.surfaceHi; }}
              onMouseLeave={e => { e.currentTarget.style.color = T.textMuted; e.currentTarget.style.background = 'transparent'; }}>
              {item.label}
            </button>
          ))}
          <NotificationBell token={token} T={T} />
          {isMobile
            ? <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7, padding: '7px 11px', cursor: 'pointer', color: T.textMuted, fontSize: 14 }}>{menuOpen ? '×' : '≡'}</button>
            : <button onClick={async () => { await logout(); navigate('/'); }} className="mino-nav-link" style={{ color: T.textMuted }}
                onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.background = T.surfaceHi; }}
                onMouseLeave={e => { e.currentTarget.style.color = T.textMuted; e.currentTarget.style.background = 'transparent'; }}>
                Sign out
              </button>
          }
        </div>
      </nav>

      {isMobile && menuOpen && (
        <div style={{ background: T.bg, borderBottom: `1px solid ${T.border}`, padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 4, animation: 'slideDown 0.18s ease', position: 'relative', zIndex: 99 }}>
          {navItems.map(item => (
            <button key={item.path} className="mino-nav-link" style={{ color: T.textMuted, textAlign: 'left', padding: '10px 12px', width: '100%' }}
              onClick={() => { navigate(item.path); setMenuOpen(false); }}
              onMouseEnter={e => e.currentTarget.style.color = T.text}
              onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>
              {item.label}
            </button>
          ))}
          <button className="mino-nav-link" onClick={async () => { await logout(); navigate('/'); }}
            style={{ textAlign: 'left', padding: '10px 12px', color: T.no, width: '100%' }}>
            Sign out
          </button>
        </div>
      )}

      {/* ═══════════ MOBILE DASHBOARD (simplified) ═══════════ */}
      {isMobile && (
        <div style={{ position: 'relative', zIndex: 1, padding: '28px 20px 100px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Skel w={140} h={11} r={4}/><Skel w="70%" h={44} r={6}/><Skel w="100%" h={48} r={10}/>
            </div>
          ) : (
            <FadeUp>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.textDim, marginBottom: 12 }}>
                Balance — {displayName}
              </p>
              <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 42, fontWeight: 700, letterSpacing: '-1.2px', color: T.text, lineHeight: 1, marginBottom: 20 }}>
                KES {parseFloat(profile?.balance || 0).toLocaleString()}
              </h1>
              <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                <button onClick={() => navigate('/deposit')}
                  style={{ flex: 1, background: T.text, color: T.bg, border: 'none', borderRadius: 12, padding: '14px 0', fontFamily: "'Outfit', sans-serif", fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                  + Deposit
                </button>
                <button onClick={() => navigate('/withdraw')}
                  style={{ flex: 1, background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 0', fontFamily: "'Outfit', sans-serif", fontWeight: 500, cursor: 'pointer', fontSize: 14 }}>
                  Withdraw
                </button>
              </div>

              {/* Quick stat strip — flat, no boxes, just soft dividers */}
              <div style={{ display: 'flex', marginBottom: 24 }}>
                {[
                  { label: 'Active',   value: activeBets.length, color: T.accent },
                  { label: 'Win rate', value: `${winRate}%`, color: winRate >= 50 ? T.yes : T.no },
                  { label: 'P&L',      value: `${realizedPnL >= 0 ? '+' : ''}KES ${Math.abs(realizedPnL) >= 1000 ? (Math.abs(realizedPnL)/1000).toFixed(1)+'K' : Math.abs(realizedPnL).toFixed(0)}`, color: realizedPnL >= 0 ? T.yes : T.no },
                ].map(({ label, value, color }, i) => (
                  <div key={label} style={{ flex: 1, textAlign: 'center', padding: '0 6px', borderLeft: i > 0 ? `1px solid ${T.border}` : 'none' }}>
                    <p style={{ fontSize: 9, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, fontFamily: "'Outfit', sans-serif" }}>{label}</p>
                    <p style={{ fontSize: 16, fontWeight: 700, color: color || T.text, fontFamily: "'Syne', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
                  </div>
                ))}
              </div>

              {streak.count >= 2 && (
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: streak.type === 'won' ? T.yes : T.no, fontFamily: "'Outfit', sans-serif" }}>
                    {streak.type === 'won' ? '🔥' : '📉'} {streak.count} {streak.type} streak
                  </span>
                </div>
              )}
            </FadeUp>
          )}

          {!loading && (
            <FadeUp delay={0.08}>
              <div style={{ height: 1, background: T.border, margin: '16px 0 20px' }} />

              <div style={{ marginBottom: 24 }}>
                <p style={{ fontSize: 10, fontWeight: 500, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 12, fontFamily: "'Outfit', sans-serif" }}>Recent activity</p>
                <RecentActivity bets={bets} />
              </div>

              <div>
                <p style={{ fontSize: 10, fontWeight: 500, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 12, fontFamily: "'Outfit', sans-serif" }}>Trending markets</p>
                <TrendingMarkets token={token} />
              </div>

              <div style={{ display: 'flex', gap: 20, marginTop: 28, justifyContent: 'center' }}>
                {[['See all trades', '/markets'], ['Leaderboard', '/leaderboard']].map(([label, path]) => (
                  <span key={path} onClick={() => navigate(path)}
                    style={{ fontSize: 13, color: T.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'Outfit', sans-serif" }}>
                    {label}
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                  </span>
                ))}
              </div>
            </FadeUp>
          )}
        </div>
      )}

      {/* ═══════════ DESKTOP DASHBOARD (full) ═══════════ */}
      {!isMobile && (
      <>
      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1, overflowX: 'hidden' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '32px 16px' : '52px 32px', animation: 'fadeIn 0.4s ease', overflowX: 'hidden', }}>

          {/* Hero */}
          <div style={{ marginBottom: 48 }}>
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Skel w={160} h={11} r={4}/><Skel w={280} h={48} r={6}/><Skel w={200} h={13} r={4}/>
              </div>
            ) : (
              <FadeUp>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.textDim, marginBottom: 20 }}>
                  Portfolio — {displayName}
                </p>
                <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'flex-end', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: 20 }}>
                  <div>
                    <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: isMobile ? 40 : 64, fontWeight: 700, letterSpacing: isMobile ? '-1px' : '-3px', color: T.text, lineHeight: 1, marginBottom: 16 }}>
                      KES {parseFloat(profile?.balance || 0).toLocaleString()}
                    </h1>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                      {unrealizedPnL !== 0 && (
                        <span style={{ fontSize: 12, fontWeight: 500, color: T.textMuted, background: T.surface, border: `1px solid ${T.border}`, padding: '4px 10px', borderRadius: 20, fontFamily: 'monospace' }}>
                          {unrealizedPnL >= 0 ? '▲' : '▼'} KES {Math.abs(unrealizedPnL).toFixed(2)} unrealized
                        </span>
                      )}
                      {streak.count >= 2 && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: streak.type === 'won' ? T.yes : T.no, background: streak.type === 'won' ? T.yesDim : T.noDim, padding: '4px 10px', borderRadius: 20, fontFamily: "'Outfit', sans-serif" }}>
                          {streak.type === 'won' ? '🔥' : '📉'} {streak.count} streak
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: T.textMuted, fontFamily: "'Outfit', sans-serif" }}>
                        <span style={{ color: T.yes, fontWeight: 500 }}>{activeBets.length} active</span> · KES {totalDeployed.toLocaleString()} deployed
                      </span>
                      {roi !== 0 && (
                        <span style={{ fontSize: 12, color: roi >= 0 ? T.yes : T.no, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>
                          {roi >= 0 ? '+' : ''}{roi.toFixed(1)}% ROI
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => navigate('/deposit')}
                      style={{ background: T.text, color: T.bg, border: 'none', borderRadius: 10, padding: '10px 22px', fontFamily: "'Outfit', sans-serif", fontWeight: 600, cursor: 'pointer', fontSize: 13, transition: 'opacity 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                      onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                      + Deposit
                    </button>
                    <button onClick={() => navigate('/withdraw')}
                      style={{ background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 22px', fontFamily: "'Outfit', sans-serif", fontWeight: 400, cursor: 'pointer', fontSize: 13, transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = T.textMuted; e.currentTarget.style.color = T.text; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textMuted; }}>
                      Withdraw
                    </button>
                  </div>
                </div>
              </FadeUp>
            )}
          </div>

          <div style={{ height: 1, background: T.border, marginBottom: 40 }} />

          {/* Key Metrics */}
          <FadeUp delay={0.05}>
            <div className={isMobile ? 'horizontal-scroll' : undefined} style={{ display: isMobile ? 'flex' : 'grid', gridTemplateColumns: 'repeat(5,1fr)', flexDirection: isMobile ? 'row' : undefined, scrollbarWidth: 'none', gap: 1, marginBottom: 40, border: `1px solid ${T.border}`, borderRadius: 14, overflowX: isMobile ? 'auto' : 'visible' }}>
              {loading ? [1,2,3,4,5].map(i => (
                <div key={i} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8, borderRight: `1px solid ${T.border}` }}>
                  <Skel w="55%" h={10}/><Skel w="75%" h={22} r={4}/><Skel w="40%" h={10}/>
                </div>
              )) : [
                { label: 'Total Trades',   value: stats?.total_bets || 0,          sub: `${activeBets.length} active` },
                { label: 'Win Rate',       value: `${winRate}%`,                   sub: `${stats?.won_bets || 0}W · ${lostBets.length}L`, color: winRate >= 50 ? T.yes : T.no },
                { label: 'Total Staked',   value: `KES ${totalStaked >= 1000 ? (totalStaked/1000).toFixed(1)+'K' : totalStaked.toFixed(0)}`, sub: 'All time' },
                { label: 'Total Returned', value: `KES ${totalReturned >= 1000 ? (totalReturned/1000).toFixed(1)+'K' : totalReturned.toFixed(0)}`, sub: `${wonBets.length + soldBets.length} payouts`, color: T.yes },
                { label: 'Realized P&L',   value: `${realizedPnL >= 0 ? '+' : ''}KES ${Math.abs(realizedPnL) >= 1000 ? (Math.abs(realizedPnL)/1000).toFixed(1)+'K' : Math.abs(realizedPnL).toFixed(0)}`, sub: 'Won + Sold', color: realizedPnL >= 0 ? T.yes : T.no },
              ].map(({ label, value, sub, color }, idx, arr) => (
                <div key={label} className="stat-card" style={{ padding: isMobile ? '16px 20px' : '24px 28px', borderRight: idx < arr.length - 1 ? `1px solid ${T.border}` : 'none', flexShrink: 0, minWidth: isMobile ? 160 : 0, background: T.surface }}>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textDim, marginBottom: 10 }}>{label}</p>
                  <p style={{ fontFamily: "'Syne', sans-serif", fontSize: isMobile ? 16 : 22, fontWeight: 700, color: color || T.text, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: T.textDim, fontWeight: 300 }}>{sub}</p>
                </div>
              ))}
            </div>
          </FadeUp>

          {/* Tabs */}
          <FadeUp delay={0.1}>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>

              {/* Tab header */}
              <div style={{ borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', padding: '0 20px', background: T.surface }}>
                {TABS.map(tab => (
                  <button key={tab.key} className="tab-btn" onClick={() => setActiveTab(tab.key)}
                    style={{ padding: '16px 18px', background: 'transparent', border: 'none', borderBottom: `2px solid ${activeTab === tab.key ? T.text : 'transparent'}`, color: activeTab === tab.key ? T.text : T.textMuted, fontWeight: activeTab === tab.key ? 600 : 400, cursor: 'pointer', fontSize: 13, fontFamily: "'Outfit', sans-serif", marginBottom: -1, whiteSpace: 'nowrap' }}>
                    {tab.label}
                    {tab.count !== undefined && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: activeTab === tab.key ? T.textMuted : T.textDim, fontFamily: 'monospace' }}>{tab.count}</span>
                    )}
                  </button>
                ))}
                {activeTab === 'trades' && bets.length > 0 && (
  <div style={{
    marginLeft: 'auto',
    display: 'flex',
    gap: 4,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    maxWidth: isMobile ? 160 : 'none',
  }}>
                    {[['all','All'],['active','Open'],['won','Won'],['lost','Lost'],['sold','Sold']].map(([val, label]) => (
                      <button key={val} onClick={() => setBetFilter(val)}
                        style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${betFilter === val ? T.textMuted : T.border}`, background: betFilter === val ? T.surfaceHi : 'transparent', color: betFilter === val ? T.text : T.textMuted, cursor: 'pointer', fontSize: 11, fontFamily: "'Outfit', sans-serif", transition: 'all 0.12s' }}>
                        {label} <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{val === 'all' ? bets.length : bets.filter(b => b.status === val).length}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Portfolio Tab */}
              {activeTab === 'portfolio' && (
  <div style={{ padding: isMobile ? '20px 16px' : '28px 24px', overflow: 'hidden', width: '100%', boxSizing: 'border-box' }}>
                  {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <Skel w="100%" h={140} r={8}/>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Skel w="100%" h={100} r={8}/><Skel w="100%" h={100} r={8}/>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, width: '100%', minWidth: 0 }}>
<div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '200px 1fr', gap: 14, alignItems: 'start' }}>

                        {/* Left col */}
<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
  {stats?.total_bets > 0 && (
    <div style={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start' }}>
      <div style={{ width: '100%', maxWidth: isMobile ? 280 : '100%' }}>
        <WinRateDonut stats={stats} activeBets={activeBets} lostBets={lostBets} soldBets={soldBets} />
      </div>
    </div>
  )}

                          <div style={{
  display: 'grid',
  gridTemplateColumns: 'repeat(2,1fr)',
  gap: 8,
}}>
  {[
    { label: 'Avg stake',   value: bets.length > 0 ? `KES ${(totalStaked / bets.length).toFixed(0)}` : 'KES 0' },
    { label: 'Streak',      value: streak.count >= 1 ? `${streak.count} ${streak.type}` : '—', color: streak.count >= 2 ? (streak.type === 'won' ? T.yes : T.no) : T.text },
    { label: 'Deployed',    value: `KES ${totalDeployed.toFixed(0)}` },
    { label: 'Open trades', value: activeBets.length, color: activeBets.length > 0 ? T.accent : T.text },
  ].map(({ label, value, color }) => (
    <div key={label} style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: '12px 14px', minWidth: 0 }}>
      <p style={{ fontSize: 10, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>{label}</p>
      <p style={{ fontSize: 15, fontWeight: 700, color: color || T.text, fontFamily: "'Syne', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
    </div>
  ))}
</div>

                          {/* Win rate by category */}
                          {(() => {
                            const map = {};
                            bets.forEach(b => {
                              const cat = b.category || 'Other';
                              if (!map[cat]) map[cat] = { won: 0, total: 0 };
                              map[cat].total++;
                              if (b.status === 'won') map[cat].won++;
                            });
                            const entries = Object.entries(map)
                              .filter(([, d]) => d.total >= 1)
                              .map(([cat, d]) => ({ cat, rate: Math.round((d.won / d.total) * 100), won: d.won, total: d.total }))
                              .sort((a, b) => b.rate - a.rate);
                            if (!entries.length) return null;
                            const COLORS = ['#3772ff','#16c784','#f3a94a','#9b5de5','#ea3943'];
                            return (
                             <div className="scroll-container" style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px', maxHeight: isMobile ? 220 : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
                                <p style={{ fontSize: 10, fontWeight: 500, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 14, fontFamily: "'Outfit', sans-serif" }}>Win rate by category</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                  {entries.map(({ cat, rate, won, total }) => (
                                    <div key={cat}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                        <span style={{ fontSize: 12, color: T.text, fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}>{cat}</span>
                                        <span style={{ fontSize: 11, color: T.textDim, fontFamily: "'Outfit', sans-serif" }}>{won}/{total} · <span style={{ color: T.text, fontWeight: 500 }}>{rate}%</span></span>
                                      </div>
                                      <div style={{ height: 2, borderRadius: 1, background: T.border, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${rate}%`, borderRadius: 1, background: T.text, opacity: 0.6, transition: 'width 0.6s cubic-bezier(0.16,1,0.3,1)' }} />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          <CollapsibleSection title="Trending markets" isMobile={isMobile}>
                            <TrendingMarkets token={token} />
                          </CollapsibleSection>
                          <CollapsibleSection title="Recent activity" isMobile={isMobile}>
                            <RecentActivity bets={bets} />
                          </CollapsibleSection>
                        </div>

                        {/* Right col */}
<div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, overflow: 'hidden' }}>

                          {/* P&L Chart */}
                          <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px 20px 14px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textDim }}>Cumulative P&L</p>
                              <span style={{ fontSize: 16, fontWeight: 700, color: realizedPnL >= 0 ? T.yes : T.no, fontFamily: "'Syne', sans-serif", letterSpacing: '-0.5px' }}>
                                {realizedPnL >= 0 ? '+' : ''}KES {realizedPnL.toFixed(2)}
                              </span>
                            </div>
                            <PnLChart bets={bets} T={T} />
                          </div>

                          {/* Open positions */}
                          {activeBets.length > 0 && (
                            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
                              <div className="scroll-container" style={{ overflowX: isMobile ? 'auto' : 'visible', overflowY: isMobile ? 'auto' : 'visible', maxHeight: isMobile ? 320 : 'none' }}>
                                <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
                                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textDim }}>
                                    Open positions · <span style={{ color: T.accent }}>{activeBets.length}</span> active · KES {totalDeployed.toLocaleString()} deployed
                                  </p>
                                </div>
                                {!isMobile && (
                                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 100px', gap: 12, padding: '8px 20px', borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                                    {['Market', 'Stake', 'If correct now', 'Date', 'Status'].map(h => (
                                      <p key={h} style={{ fontSize: 10, fontWeight: 500, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'Outfit', sans-serif" }}>{h}</p>
                                    ))}
                                  </div>
                                )}
                                {activeBets.map(bet => <BetRow key={bet.id} bet={bet} isMobile={isMobile} />)}
                              </div>
                            </div>
                          )}

                          {/* Trade highlights */}
                          <div className="scroll-container" style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px', maxHeight: isMobile ? 200 : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
                            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textDim, marginBottom: 16 }}>Trade highlights</p>
                            {bestTrade ? (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p style={{ fontSize: 10, color: T.textDim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>Best trade</p>
                                  <p style={{ fontSize: 12, color: T.textMuted, fontWeight: 400, lineHeight: 1.4, fontFamily: "'Outfit', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{bestTrade.title}</p>
                                </div>
                                <p style={{ fontSize: 13, fontWeight: 700, color: T.yes, fontFamily: "'Syne', sans-serif", letterSpacing: '-0.5px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                  +KES {(parseFloat(bestTrade.payout||0) - parseFloat(bestTrade.amount||0)).toFixed(0)}
                                </p>
                              </div>
                            ) : null}
                            {worstTrade ? (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p style={{ fontSize: 10, color: T.textDim, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>Biggest loss</p>
                                  <p style={{ fontSize: 12, color: T.textMuted, fontWeight: 400, lineHeight: 1.4, fontFamily: "'Outfit', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{worstTrade.title}</p>
                                </div>
                                <p style={{ fontSize: 13, fontWeight: 700, color: T.no, fontFamily: "'Syne', sans-serif", letterSpacing: '-0.5px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                  -KES {parseFloat(worstTrade.amount||0).toFixed(0)}
                                </p>
                              </div>
                            ) : null}
                            {!bestTrade && !worstTrade && (
                              <p style={{ color: T.textDim, fontSize: 12, textAlign: 'center', padding: '8px 0', fontFamily: "'Outfit', sans-serif" }}>No resolved trades yet</p>
                            )}
                          </div>

                          {/* Closing soon */}
                          {(() => {
                            const upcoming = activeBets
                              .filter(b => b.closes_at)
                              .map(b => ({ ...b, daysLeft: Math.ceil((new Date(b.closes_at) - Date.now()) / 86400000) }))
                              .sort((a, b) => a.daysLeft - b.daysLeft)
                              .slice(0, 4);
                            if (!upcoming.length) return null;
                            return (
                              <div className="scroll-container" style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? '16px' : '20px', maxHeight: isMobile ? 240 : 'none', overflowY: isMobile ? 'auto' : 'visible' }}>
                                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textDim, marginBottom: 14 }}>Closing soon</p>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  {upcoming.map((b, i) => (
                                    <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < upcoming.length - 1 ? `1px solid ${T.border}` : 'none', overflow: 'hidden' }}>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontSize: 11, color: T.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Outfit', sans-serif" }}>{b.title}</p>
                                        <p style={{ fontSize: 10, color: T.textDim, marginTop: 1, fontFamily: "'Outfit', sans-serif" }}>KES {parseFloat(b.amount).toFixed(0)} · {b.prediction?.toUpperCase()}</p>
                                      </div>
                                      <span style={{ fontSize: 10, fontWeight: 700, fontFamily: 'monospace', padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0, background: b.daysLeft <= 1 ? T.noDim : b.daysLeft <= 3 ? `${T.gold}18` : T.accentDim, color: b.daysLeft <= 1 ? T.no : b.daysLeft <= 3 ? T.gold : T.accent }}>
                                        {b.daysLeft <= 0 ? 'today' : `${b.daysLeft}d`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Ticker */}
                      <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', height: 36, display: 'flex', alignItems: 'center' }}>
                        <div className="ticker-track">
                          {[
                            { label: '@MinoMarkets254', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.735-8.849L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                            { label: 'Kenyan prediction markets · Real events', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
                            { label: 'Deposits & withdrawals via M-Pesa', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></svg> },
                            { label: 'Win rate tracked · P&L real time', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg> },
                            { label: 'Sports · Politics · Entertainment · Global', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg> },
                            { label: '20% WHT on gains only · Transparent', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
                            { label: '@MinoMarkets254', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.735-8.849L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                            { label: 'Kenyan prediction markets · Real events', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
                            { label: 'Deposits & withdrawals via M-Pesa', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="14" x="2" y="5" rx="2"/><path d="M2 10h20"/></svg> },
                            { label: 'Win rate tracked · P&L real time', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg> },
                            { label: 'Sports · Politics · Entertainment · Global', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg> },
                            { label: '20% WHT on gains only · Transparent', icon: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
                          ].map((item, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '0 18px', fontSize: 11, color: T.textDim, borderRight: `1px solid ${T.border}`, fontFamily: "'Outfit', sans-serif", flexShrink: 0 }}>
                              <span style={{ opacity: 0.5 }}>{item.icon}</span>{item.label}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* Quick actions */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[['Browse Markets', '/markets'], ['Leaderboard', '/leaderboard'], ['Profile', '/profile']].map(([label, path]) => (
                          <span key={path} onClick={() => navigate(path)}
                            style={{ fontSize: 13, color: T.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'Outfit', sans-serif", transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.gap = '8px'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = T.textMuted; e.currentTarget.style.gap = '5px'; }}>
                            {label}
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Trades Tab */}
              {activeTab === 'trades' && (
  <div style={{ overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
                  {!isMobile && bets.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 100px', gap: 12, padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: T.surface }}>
                      {['Market', 'Stake', 'Return / P&L', 'Date', 'Status'].map(h => (
                        <p key={h} style={{ fontSize: 10, fontWeight: 500, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'Outfit', sans-serif" }}>{h}</p>
                      ))}
                    </div>
                  )}
                  {loading
                    ? [1,2,3].map(i => (
                      <div key={i} style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}><Skel w="70%" h={12}/><Skel w="30%" h={10}/></div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}><Skel w={70} h={12}/><Skel w={50} h={16} r={4}/></div>
                      </div>
                    ))
                    : filteredBets.length === 0
                      ? <div style={{ padding: '56px 24px', textAlign: 'center' }}>
                          <p style={{ color: T.textDim, fontSize: 14, marginBottom: 20, fontFamily: "'Outfit', sans-serif" }}>No trades yet</p>
                          <button onClick={() => navigate('/markets')} style={{ background: T.text, color: T.bg, border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: "'Outfit', sans-serif" }}>Browse Markets</button>
                        </div>
                      : filteredBets.map(bet => <BetRow key={bet.id} bet={bet} isMobile={isMobile} />)
                  }
                </div>
              )}

              {/* Saved Tab */}
              {activeTab === 'saved' && (
                loading
                  ? [1,2,3].map(i => (
                    <div key={i} style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 12, alignItems: 'center' }}>
                      <Skel w={36} h={36} r={8}/><div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}><Skel w="60%" h={12}/><Skel w="30%" h={10}/></div>
                    </div>
                  ))
                  : savedMarkets.length === 0
                    ? <div style={{ padding: '56px 24px', textAlign: 'center' }}>
                        <div style={{ marginBottom: 16, opacity: 0.2 }}>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>
                        </div>
                        <p style={{ color: T.textMuted, fontSize: 14, marginBottom: 8, fontFamily: "'Outfit', sans-serif" }}>No saved markets</p>
                        <p style={{ color: T.textDim, fontSize: 12, marginBottom: 20, fontFamily: "'Outfit', sans-serif" }}>Bookmark markets to track them here</p>
                        <button onClick={() => navigate('/markets')} style={{ background: T.text, color: T.bg, border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 13, fontFamily: "'Outfit', sans-serif" }}>Browse Markets</button>
                      </div>
                    : savedMarkets.map(market => <SavedMarketRow key={market.id} market={market} onUnsave={handleUnsave} />)
              )}

            </div>
          </FadeUp>
        </div>

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${T.border}`, padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: T.textDim, fontWeight: 300 }}>© 2026 Mino · DAFCOM ENTERPRISES</span>
          <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: T.textDim, cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = T.textMuted}
            onMouseLeave={e => e.currentTarget.style.color = T.textDim}>
            Back to markets
          </span>
        </div>
      </div>
      </>
      )}

      {isMobile && <BottomNav />}
    </div>
  );
}